import { useEffect, useState } from 'react';

/**
 * useOfflineStatus — detecta si el navegador está sin conexión o si la red es lenta.
 *
 * Devuelve `{ isOffline, offlineSince, isSlow, effectiveType }`.
 * - `isOffline`: booleano basado en `navigator.onLine` + eventos online/offline.
 * - `offlineSince`: Date del momento en que se perdió la conexión (null si online).
 * - `isSlow`: `true` cuando `navigator.connection.effectiveType` es `slow-2g` o `2g`.
 *   Requiere Network Information API (Chrome/Edge/Android). Safari no la soporta
 *   y devolverá siempre `false` — degradación limpia.
 * - `effectiveType`: `slow-2g` | `2g` | `3g` | `4g` | null (si el API no está).
 *
 * `navigator.onLine` es imperfecto (algunos navegadores lo reportan `true`
 * incluso con red bloqueada), pero es la señal más fiable a nivel de cliente
 * sin hacer polling. Los eventos `online`/`offline` sí son de confianza para
 * transiciones reales.
 */
export function useOfflineStatus() {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );
  const [offlineSince, setOfflineSince] = useState(
    typeof navigator !== 'undefined' && !navigator.onLine ? new Date() : null
  );
  const [effectiveType, setEffectiveType] = useState(() => {
    if (typeof navigator === 'undefined') return null;
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return c?.effectiveType ?? null;
  });

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setOfflineSince(null);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setOfflineSince(new Date());
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Network Information API — cambios de calidad de red (2G → 4G, etc.)
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const handleConnChange = () => setEffectiveType(conn?.effectiveType ?? null);
    conn?.addEventListener?.('change', handleConnChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      conn?.removeEventListener?.('change', handleConnChange);
    };
  }, []);

  const isSlow = effectiveType === 'slow-2g' || effectiveType === '2g';

  return { isOffline, offlineSince, isSlow, effectiveType };
}

